import { AuditLogsTable } from '@valiant/ui-shared'
import { useGetAuditLogsQuery } from '@/generated/graphql'
import { useMemo } from 'react'
import { useQueryParams } from '@valiant/ui-shared'

interface LogsTableProps {
  userId?: string
  debouncedSearch: string
}

export function LogsTable({ userId, debouncedSearch }: LogsTableProps) {
  const queryParams = useQueryParams()

  // Get pagination state from query params with defaults
  const pageSize = parseInt(queryParams.pageSize || '25', 10)
  const currentCursor = queryParams.cursor || null
  const direction = (queryParams.direction as 'forward' | 'backward') || 'forward'

  const filter = useMemo(() => {
    interface FilterObject {
      userId?: string
      action?: string
    }

    const filterObj: FilterObject = {}
    if (userId) filterObj.userId = userId
    if (debouncedSearch.trim()) filterObj.action = debouncedSearch.trim()
    return Object.keys(filterObj).length > 0 ? filterObj : undefined
  }, [debouncedSearch, userId])

  const pagination = useMemo(
    () => ({
      first: direction === 'forward' ? pageSize : undefined,
      after: direction === 'forward' ? currentCursor : undefined,
      last: direction === 'backward' ? pageSize : undefined,
      before: direction === 'backward' ? currentCursor : undefined,
    }),
    [direction, pageSize, currentCursor],
  )

  const [{ data: auditLogsData, fetching: _auditLogsLoading, error: _auditLogsError }] =
    useGetAuditLogsQuery({
      variables: { filter, pagination },
    })

  const auditLogs = auditLogsData?.getAuditLogs.nodes || []
  const pageInfo = auditLogsData?.getAuditLogs.pageInfo
  const totalCount = auditLogsData?.getAuditLogs.totalCount

  // Pagination handlers
  return (
    <AuditLogsTable
      logs={auditLogs}
      totalCount={totalCount}
      pageSize={pageSize}
      pageInfo={pageInfo}
      onNextPage={() => {
        if (pageInfo?.hasNextPage && pageInfo.endCursor) {
          queryParams.updatePagination(pageSize.toString(), pageInfo.endCursor, 'forward')
        }
      }}
      onPreviousPage={() => {
        if (pageInfo?.hasPreviousPage && pageInfo.startCursor) {
          queryParams.updatePagination(pageSize.toString(), pageInfo.startCursor, 'backward')
        }
      }}
      onPageSizeChange={(newSize: number) => {
        queryParams.updatePagination(newSize.toString())
      }}
    />
  )
}
